import { useEffect, useState, type FormEvent } from 'react';
import { Card } from '../components/ui/Card';
import {
  type AppClient,
  type AppSite,
  type AppTechnician,
  type AppWorkOrderDetail,
  type AppWorkOrderFilters,
  type AppWorkOrderStatus,
} from './data/appApi.ts';
import {
  useAppClients,
  useAppSites,
  useAppTechnicians,
  useAppWorkOrders,
  useCreateAppWorkOrder,
  useDuplicateAppWorkOrder,
  useUpdateAppWorkOrder,
} from './data/useAppData.ts';

type AppWorkOrdersWorkspaceProps = {
  enabled: boolean;
  currentUserRole?: string | null;
};

type FilterState = {
  status: AppWorkOrderStatus | 'all';
  technicianId: string;
  clientId: string;
  query: string;
};

type EditorState = {
  clientId: string;
  siteId: string;
  technicianId: string;
  title: string;
  status: AppWorkOrderStatus;
  scheduledStart: string;
  scheduledEnd: string;
  revenueEuros: string;
};

const STATUS_OPTIONS: Array<{ value: AppWorkOrderStatus; label: string }> = [
  { value: 'scheduled', label: 'Planifiée' },
  { value: 'on_site', label: 'Sur site' },
  { value: 'in_review', label: 'En revue' },
  { value: 'rework_required', label: 'Reprise demandée' },
  { value: 'ready_to_invoice', label: 'Prête à facturer' },
  { value: 'invoiced', label: 'Facturée' },
];

const EURO_FORMATTER = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const EMPTY_FILTERS: FilterState = {
  status: 'all',
  technicianId: '',
  clientId: '',
  query: '',
};

const EMPTY_EDITOR: EditorState = {
  clientId: '',
  siteId: '',
  technicianId: '',
  title: '',
  status: 'scheduled',
  scheduledStart: '',
  scheduledEnd: '',
  revenueEuros: '0',
};

const PAGE_SIZE = 4;

function statusLabel(status: AppWorkOrderStatus): string {
  return STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

function statusTone(status: AppWorkOrderStatus): string {
  switch (status) {
    case 'scheduled':
      return 'status-pill status-pill--info';
    case 'on_site':
      return 'status-pill status-pill--warning';
    case 'in_review':
      return 'status-pill status-pill--review';
    case 'rework_required':
      return 'status-pill status-pill--danger';
    case 'ready_to_invoice':
      return 'status-pill status-pill--ready';
    case 'invoiced':
      return 'status-pill status-pill--success';
  }
}

function normalizeFilters(filters: FilterState): AppWorkOrderFilters {
  return {
    status: filters.status === 'all' ? undefined : filters.status,
    technicianId: filters.technicianId || undefined,
    clientId: filters.clientId || undefined,
    query: filters.query.trim() || undefined,
  };
}

function formatScheduleWindow(order: AppWorkOrderDetail): string {
  if (!order.scheduledStart && !order.scheduledEnd) return 'À planifier';
  if (!order.scheduledStart) return `Fin ${formatDateTime(order.scheduledEnd)}`;
  if (!order.scheduledEnd) return `Début ${formatDateTime(order.scheduledStart)}`;
  return `${formatDateTime(order.scheduledStart)} → ${formatDateTime(order.scheduledEnd)}`;
}

function formatDateTime(value: string | null): string {
  if (!value) return 'N/D';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return DATE_TIME_FORMATTER.format(parsed);
}

function toEditorState(order: AppWorkOrderDetail): EditorState {
  return {
    clientId: order.client.id,
    siteId: order.site.id,
    technicianId: order.technician?.id ?? '',
    title: order.title,
    status: order.status,
    scheduledStart: toDateTimeLocalValue(order.scheduledStart),
    scheduledEnd: toDateTimeLocalValue(order.scheduledEnd),
    revenueEuros: String(order.revenueCents / 100),
  };
}

function toDateTimeLocalValue(value: string | null): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 16);
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function firstClientId(clients: AppClient[] | undefined, fallbackClientId = ''): string {
  if (fallbackClientId) return fallbackClientId;
  return clients?.[0]?.id ?? '';
}

function firstSiteId(sites: AppSite[] | undefined, fallbackSiteId = ''): string {
  if (fallbackSiteId) return fallbackSiteId;
  return sites?.[0]?.id ?? '';
}

function toRevenueCents(value: string): number {
  const normalized = value.replace(',', '.').trim();
  if (!normalized) return 0;
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error('Le montant HT doit être un nombre positif.');
  }
  return Math.round(parsed * 100);
}

function messageFromError(error: unknown): string {
  return error instanceof Error ? error.message : 'Erreur inconnue.';
}

export function AppWorkOrdersWorkspace({ enabled, currentUserRole }: AppWorkOrdersWorkspaceProps) {
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editorState, setEditorState] = useState<EditorState>(EMPTY_EDITOR);
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'danger' | 'info'; text: string } | null>(null);

  const workOrderFilters = normalizeFilters(filters);
  const clientsQuery = useAppClients(enabled);
  const techniciansQuery = useAppTechnicians(enabled);
  const workOrdersQuery = useAppWorkOrders(workOrderFilters, enabled);
  const sitesQuery = useAppSites(editorState.clientId || undefined, enabled);
  const createMutation = useCreateAppWorkOrder();
  const duplicateMutation = useDuplicateAppWorkOrder();
  const updateMutation = useUpdateAppWorkOrder();

  const clients = clientsQuery.data ?? [];
  const technicians = techniciansQuery.data ?? [];
  const workOrders = workOrdersQuery.data ?? [];
  const sites = sitesQuery.data ?? [];
  const totalPages = Math.max(1, Math.ceil(Math.max(workOrders.length, 1) / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pagedWorkOrders = workOrders.slice(pageStart, pageStart + PAGE_SIZE);
  const selectedWorkOrder = workOrders.find((order) => order.id === selectedWorkOrderId) ?? null;
  const isBusy = createMutation.isPending || duplicateMutation.isPending || updateMutation.isPending;
  const writeEnabled = currentUserRole === 'admin' || currentUserRole === 'dispatch' || currentUserRole === 'backoffice';
  const visibleRevenueCents = workOrders.reduce((sum, item) => sum + item.revenueCents, 0);
  const resourceErrors = [
    clientsQuery.error ? `Clients: ${messageFromError(clientsQuery.error)}` : null,
    techniciansQuery.error ? `Techniciens: ${messageFromError(techniciansQuery.error)}` : null,
    sitesQuery.error ? `Sites: ${messageFromError(sitesQuery.error)}` : null,
  ].filter((value): value is string => Boolean(value));

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  useEffect(() => {
    if (!enabled) return;
    if (!editorState.clientId && clients.length > 0) {
      setEditorState((current) => ({
        ...current,
        clientId: firstClientId(clients, filters.clientId),
      }));
    }
  }, [enabled, clients, editorState.clientId, filters.clientId]);

  useEffect(() => {
    if (!enabled) return;
    if (!editorState.clientId) return;
    if (sites.length === 0) return;
    const hasCurrentSite = sites.some((site) => site.id === editorState.siteId);
    if (!hasCurrentSite) {
      setEditorState((current) => ({
        ...current,
        siteId: firstSiteId(sites),
      }));
    }
  }, [enabled, editorState.clientId, editorState.siteId, sites]);

  useEffect(() => {
    if (workOrders.length === 0) {
      setSelectedWorkOrderId(null);
      if (editorMode === 'edit') {
        setEditorMode('create');
      }
      return;
    }
    if (editorMode === 'create') return;
    if (!selectedWorkOrderId || !workOrders.some((order) => order.id === selectedWorkOrderId)) {
      setSelectedWorkOrderId(workOrders[0].id);
    }
  }, [editorMode, selectedWorkOrderId, workOrders]);

  useEffect(() => {
    if (editorMode !== 'edit' || !selectedWorkOrder) return;
    setEditorState(toEditorState(selectedWorkOrder));
  }, [editorMode, selectedWorkOrder?.id]);

  useEffect(() => {
    if (editorMode !== 'edit' || !selectedWorkOrderId) return;
    const selectedIndex = workOrders.findIndex((order) => order.id === selectedWorkOrderId);
    if (selectedIndex === -1) return;
    const nextPage = Math.floor(selectedIndex / PAGE_SIZE) + 1;
    if (nextPage !== page) {
      setPage(nextPage);
    }
  }, [editorMode, page, selectedWorkOrderId, workOrders]);

  function updateFilter<K extends keyof FilterState>(key: K, value: FilterState[K]) {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  }

  function updateEditor<K extends keyof EditorState>(key: K, value: EditorState[K]) {
    setEditorState((current) => ({ ...current, [key]: value }));
  }

  function openCreateEditor() {
    if (!writeEnabled) {
      setFeedback({
        tone: 'info',
        text: 'Le profil courant est en lecture seule sur l’espace de travail.',
      });
      return;
    }
    setEditorMode('create');
    setSelectedWorkOrderId(null);
    setEditorState({
      ...EMPTY_EDITOR,
      clientId: firstClientId(clients, filters.clientId),
      siteId: '',
      technicianId: filters.technicianId,
    });
    setFeedback({
        tone: 'info',
        text: 'Mode création activé. Prépare un nouveau dossier persistant.',
      });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!writeEnabled) {
      setFeedback({
        tone: 'info',
        text: 'Le profil courant est en lecture seule sur l’espace de travail.',
      });
      return;
    }
    setFeedback(null);

    const payload = {
      clientId: editorState.clientId,
      siteId: editorState.siteId,
      technicianId: editorState.technicianId || null,
      title: editorState.title,
      scheduledStart: editorState.scheduledStart || null,
      scheduledEnd: editorState.scheduledEnd || null,
      revenueCents: toRevenueCents(editorState.revenueEuros),
      status: editorState.status,
    } as const;

    try {
      if (editorMode === 'create') {
        const created = await createMutation.mutateAsync(payload);
        setSelectedWorkOrderId(created.id);
        setEditorMode('edit');
        setEditorState(toEditorState(created));
        setFeedback({
          tone: 'success',
          text: `Dossier ${created.id} créé et ajouté à la file persistée.`,
        });
        return;
      }

      if (!selectedWorkOrderId) {
        throw new Error('Aucun dossier sélectionné pour la mise à jour.');
      }

      const updated = await updateMutation.mutateAsync({
        workOrderId: selectedWorkOrderId,
        payload,
      });
      setSelectedWorkOrderId(updated.id);
      setEditorState(toEditorState(updated));
        setFeedback({
          tone: 'success',
          text: `Dossier ${updated.id} mis à jour côté serveur.`,
        });
    } catch (error) {
      setFeedback({
        tone: 'danger',
        text: messageFromError(error),
      });
    }
  }

  async function handleDuplicateSelected() {
    if (!writeEnabled) {
      setFeedback({
        tone: 'info',
        text: 'Le profil courant est en lecture seule sur l’espace de travail.',
      });
      return;
    }
    if (!selectedWorkOrderId) {
      setFeedback({
        tone: 'danger',
        text: 'Aucun dossier sélectionné à dupliquer.',
      });
      return;
    }

    try {
      const duplicated = await duplicateMutation.mutateAsync(selectedWorkOrderId);
      setSelectedWorkOrderId(duplicated.id);
      setEditorMode('edit');
      setEditorState(toEditorState(duplicated));
      setFeedback({
        tone: 'success',
        text: `Dossier ${duplicated.id} dupliqué et persisté.`,
      });
    } catch (error) {
      setFeedback({
        tone: 'danger',
        text: messageFromError(error),
      });
    }
  }

  if (!enabled) {
    return (
      <section className="page-stack">
        <Card title="Dossiers persistés" subtitle="L’espace produit est disponible quand l’API locale est active.">
          <div className="alert-card alert-card--info">
            <strong>Mode local détecté</strong>
            <span>Basculer l’app en mode API pour gérer les dossiers persistés, les filtres et l’édition rapide.</span>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section className="page-stack">
      <section className="page-intro">
        <div>
          <div className="eyebrow">Espace dossiers</div>
          <h2>Surface produit branchée sur les données persistées</h2>
        </div>
        <p>Cette tranche sort du cockpit narratif et ouvre une vraie liste filtrable, éditable et multi-dossiers.</p>
      </section>

      <div className="mini-metric-grid">
        <article className="mini-metric">
          <span className="mini-metric__label">Dossiers visibles</span>
          <strong>{workOrders.length}</strong>
          <span>Filtres appliqués côté API.</span>
        </article>
        <article className="mini-metric">
          <span className="mini-metric__label">Valeur visible</span>
          <strong>{EURO_FORMATTER.format(visibleRevenueCents / 100)}</strong>
          <span>Total HT sur la sélection courante.</span>
        </article>
        <article className="mini-metric">
          <span className="mini-metric__label">Création / édition</span>
          <strong>{editorMode === 'create' ? 'Création' : 'Édition'}</strong>
          <span>{editorMode === 'create' ? 'Nouveau dossier' : 'Dossier sélectionné synchronisé'}</span>
        </article>
      </div>

      <Card
        title="Filtres de portefeuille"
        subtitle="Recherche, statut, technicien et client persistés."
        actions={
          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              setFilters(EMPTY_FILTERS);
              setPage(1);
            }}
          >
            Réinitialiser les filtres
          </button>
        }
      >
        <div className="app-workspace__filters">
          <label className="field-stack">
            <span>Recherche</span>
            <input
              value={filters.query}
              onChange={(event) => updateFilter('query', event.target.value)}
              placeholder="WO, client, site, technicien"
            />
          </label>
          <label className="field-stack">
            <span>Statut</span>
            <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value as FilterState['status'])}>
              <option value="all">Tous</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field-stack">
            <span>Technicien</span>
            <select value={filters.technicianId} onChange={(event) => updateFilter('technicianId', event.target.value)}>
              <option value="">Tous</option>
              {technicians.map((technician) => (
                <option key={technician.id} value={technician.id}>
                  {technician.displayName}
                </option>
              ))}
            </select>
          </label>
          <label className="field-stack">
            <span>Client</span>
            <select value={filters.clientId} onChange={(event) => updateFilter('clientId', event.target.value)}>
              <option value="">Tous</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      {feedback ? (
        <div className={`alert-card alert-card--${feedback.tone}`}>
          <strong>{feedback.tone === 'danger' ? 'Action en échec' : 'Données métier'}</strong>
          <span>{feedback.text}</span>
        </div>
      ) : null}

      {resourceErrors.length ? (
        <div className="alert-card alert-card--warning">
          <strong>Ressources métier incomplètes</strong>
          <span>{resourceErrors.join(' · ')}</span>
        </div>
      ) : null}

      <div className="app-workspace__layout">
        <Card
          title="Dossiers persistés"
          subtitle="Liste issue de `/api/app/work-orders`."
          actions={
            <button type="button" className="primary-button" onClick={openCreateEditor} disabled={!writeEnabled}>
              Nouveau dossier
            </button>
          }
        >
          {workOrdersQuery.isLoading || clientsQuery.isLoading || techniciansQuery.isLoading ? (
            <div className="alert-card alert-card--info">
              <strong>Chargement</strong>
              <span>Lecture des ressources applicatives en cours.</span>
            </div>
          ) : null}

          {workOrdersQuery.error ? (
            <div className="alert-card alert-card--danger">
              <strong>API indisponible</strong>
              <span>{messageFromError(workOrdersQuery.error)}</span>
            </div>
          ) : null}

          {!workOrdersQuery.error && workOrders.length === 0 ? (
            <div className="alert-card alert-card--warning">
              <strong>Aucun dossier visible</strong>
              <span>La sélection courante ne retourne aucun dossier persistant.</span>
            </div>
          ) : null}

          <div className="app-order-list">
            {pagedWorkOrders.map((order) => (
              <button
                key={order.id}
                type="button"
                className={`app-order-list__item${order.id === selectedWorkOrderId ? ' is-active' : ''}`}
                onClick={() => {
                  setSelectedWorkOrderId(order.id);
                  setEditorMode('edit');
                  setFeedback(null);
                }}
              >
                <div className="app-order-list__head">
                  <strong>{order.id}</strong>
                  <span className={statusTone(order.status)}>{statusLabel(order.status)}</span>
                </div>
                <span>{order.title}</span>
                <span>{order.client.name}</span>
                <div className="app-order-list__meta">
                  <span>{order.technician?.displayName ?? 'Non assigné'}</span>
                  <span>{formatScheduleWindow(order)}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="app-order-list__footer">
            <span>
              {workOrders.length ? `${pageStart + 1}-${Math.min(pageStart + PAGE_SIZE, workOrders.length)}` : '0'} / {workOrders.length}
            </span>
            <div className="action-row">
              <button
                type="button"
                className="secondary-button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={safePage <= 1}
              >
                Précédent
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={safePage >= totalPages}
              >
                Suivant
              </button>
            </div>
          </div>
        </Card>

        <div className="order-detail">
          <Card
            title={
              selectedWorkOrder
                ? `${selectedWorkOrder.id} · ${selectedWorkOrder.title}`
                : 'Nouveau dossier'
            }
            subtitle={
              selectedWorkOrder
                ? `${selectedWorkOrder.client.name} · ${selectedWorkOrder.site.name}`
                : 'Préparer une intervention persistée et assignable.'
            }
            actions={
              editorMode === 'edit' ? (
                <button type="button" className="secondary-button" onClick={() => void handleDuplicateSelected()} disabled={isBusy || !writeEnabled}>
                  Dupliquer vers un nouveau dossier
                </button>
              ) : null
            }
          >
            {selectedWorkOrder ? (
              <div className="detail-grid detail-grid--summary">
                <div className="detail-card">
                  <span className="detail-card__label">Statut</span>
                  <strong>{statusLabel(selectedWorkOrder.status)}</strong>
                  <span>{formatScheduleWindow(selectedWorkOrder)}</span>
                </div>
                <div className="detail-card">
                  <span className="detail-card__label">Technicien</span>
                  <strong>{selectedWorkOrder.technician?.displayName ?? 'Non assigné'}</strong>
                  <span>{selectedWorkOrder.technician?.zone ?? 'Affectation en attente'}</span>
                </div>
                <div className="detail-card">
                  <span className="detail-card__label">Client / site</span>
                  <strong>{selectedWorkOrder.client.name}</strong>
                  <span>{selectedWorkOrder.site.addressLine}, {selectedWorkOrder.site.city}</span>
                </div>
                <div className="detail-card">
                  <span className="detail-card__label">Valeur HT</span>
                  <strong>{EURO_FORMATTER.format(selectedWorkOrder.revenueCents / 100)}</strong>
                  <span>Origine {selectedWorkOrder.source} · MAJ {formatDateTime(selectedWorkOrder.updatedAt)}</span>
                </div>
              </div>
            ) : (
              <div className="alert-card alert-card--info">
                <strong>Création prête</strong>
                <span>Saisir les informations ci-dessous pour ouvrir un nouveau dossier de travail.</span>
              </div>
            )}
          </Card>

          <Card
            title={editorMode === 'create' ? 'Créer un dossier' : 'Éditer le dossier'}
            subtitle="Formulaire branché sur les ressources applicatives persistées."
          >
            {!writeEnabled ? (
              <div className="alert-card alert-card--info">
                <strong>Lecture seule</strong>
                <span>La création, la duplication et l’édition de dossiers sont réservées aux rôles planification, exploitation et administration.</span>
              </div>
            ) : null}
            <form className="app-workspace__form" onSubmit={(event) => void handleSubmit(event)}>
              <div className="app-workspace__form-grid">
                <label className="field-stack">
                  <span>Client</span>
                  <select value={editorState.clientId} onChange={(event) => updateEditor('clientId', event.target.value)} disabled={!writeEnabled}>
                    <option value="">Sélectionner</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field-stack">
                  <span>Site</span>
                  <select value={editorState.siteId} onChange={(event) => updateEditor('siteId', event.target.value)} disabled={!writeEnabled}>
                    <option value="">Sélectionner</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field-stack">
                  <span>Technicien</span>
                  <select value={editorState.technicianId} onChange={(event) => updateEditor('technicianId', event.target.value)} disabled={!writeEnabled}>
                    <option value="">Non assigné</option>
                    {technicians.map((technician: AppTechnician) => (
                      <option key={technician.id} value={technician.id}>
                        {technician.displayName}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field-stack">
                  <span>Statut</span>
                  <select value={editorState.status} onChange={(event) => updateEditor('status', event.target.value as AppWorkOrderStatus)} disabled={!writeEnabled}>
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field-stack field-stack--wide">
                  <span>Titre intervention</span>
                  <input value={editorState.title} onChange={(event) => updateEditor('title', event.target.value)} disabled={!writeEnabled} />
                </label>
                <label className="field-stack">
                  <span>CA HT (€)</span>
                  <input
                    inputMode="decimal"
                    value={editorState.revenueEuros}
                    onChange={(event) => updateEditor('revenueEuros', event.target.value)}
                    disabled={!writeEnabled}
                  />
                </label>
                <label className="field-stack">
                  <span>Début planifié</span>
                  <input
                    type="datetime-local"
                    value={editorState.scheduledStart}
                    onChange={(event) => updateEditor('scheduledStart', event.target.value)}
                    disabled={!writeEnabled}
                  />
                </label>
                <label className="field-stack">
                  <span>Fin planifiée</span>
                  <input
                    type="datetime-local"
                    value={editorState.scheduledEnd}
                    onChange={(event) => updateEditor('scheduledEnd', event.target.value)}
                    disabled={!writeEnabled}
                  />
                </label>
              </div>

              <div className="detail-actions">
                <button type="submit" className="primary-button" disabled={isBusy || !writeEnabled}>
                  {editorMode === 'create' ? 'Créer le dossier' : 'Enregistrer les changements'}
                </button>
                <button type="button" className="secondary-button" onClick={openCreateEditor} disabled={isBusy || !writeEnabled}>
                  Réinitialiser le formulaire
                </button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
}
